import * as React from 'react';
import { ActionAfterViewed } from '@cian/action-after-viewed-component';
import { useSelector } from 'react-redux';
import {
  getBuilders,
  getNewbuildingDisplayName,
  getNewbuildingId,
  getNewbuildingReviewsStat,
} from '../../selectors/newbuilding';
import { NewbuildingReviewsTeaser } from '../../components/NewbuildingReviewsTeaser';
import { getUrlPath } from '../../selectors/path';
import { EmptyReviewsTeaser, ManyReviewsTeaser } from '../../components/NewbuildingReviewsTeaser/parts';
import { getAnalyticsUser } from '../../selectors/analytics';
import { getProductsData } from '../../selectors/analytics/builder';
import { trackNewbuildingReviewsAnalytic } from '../../utils/analytics/reviewsAnalytics';
import { MasterReviewContainer } from '../MasterReviewContainer';
import { useQuestionsFeature } from '../../hooks/useQuestionsFeature';

export const EMPTY_REVIEWS_COUNT = 0;

export const NewbuildingReviewsTeaserContainer: React.FC = () => {
  const { isQuestionsFeatureEnabled } = useQuestionsFeature();

  const reviews = useSelector(getNewbuildingReviewsStat);
  const fullUrl = useSelector(getUrlPath);
  const name = useSelector(getNewbuildingDisplayName);

  const user = useSelector(getAnalyticsUser);

  const builder = useSelector(getBuilders)[0] || null;
  const id = useSelector(getNewbuildingId);
  const products = [getProductsData(id, builder)];

  const page = {
    pageType: 'CardJK',
    siteType: 'desktop',
    extra: {
      type: 'review',
      rating: reviews?.rating,
      PageTypeJK: 'Normal',
    },
  };

  const trackingReviewsTeaserView = React.useCallback(() => {
    trackNewbuildingReviewsAnalytic({
      action: 'Show_sopr',
      category: 'Card_JK',
      label: 'NewbuildingReviews',
      user,
      page,
      products,
    });
  }, []);

  const trackingNewReviewLinkClick = React.useCallback(() => {
    trackNewbuildingReviewsAnalytic({
      action: 'Click_sopr',
      category: 'NewbuildingReviews',
      label: 'Write',
      user,
      page,
      products,
    });
  }, []);

  const trackingShowReviewsLinkClick = React.useCallback(() => {
    trackNewbuildingReviewsAnalytic({
      action: 'Click_sopr',
      category: 'NewbuildingReviews',
      label: 'All_Reviews',
      user,
      page,
      products,
    });
  }, []);

  const trackingReadAllClick = React.useCallback(() => {
    trackNewbuildingReviewsAnalytic({
      action: 'Click_sopr',
      category: 'NewbuildingReviews',
      label: 'read_more',
      user,
      page,
      products,
    });
  }, []);

  if (!reviews || reviews.reviewsCount === EMPTY_REVIEWS_COUNT) {
    return (
      <NewbuildingReviewsTeaser
        isQuestionsFeatureEnabled={isQuestionsFeatureEnabled}
        reviews={
          <EmptyReviewsTeaser
            name={name}
            fullUrl={fullUrl as string}
            newReviewLinkClick={trackingNewReviewLinkClick}
            isQuestionsFeatureEnabled={isQuestionsFeatureEnabled}
          />
        }
      />
    );
  }

  return (
    <ActionAfterViewed callback={trackingReviewsTeaserView}>
      <NewbuildingReviewsTeaser
        reviewsCountText={reviews.reviewsCountText}
        isQuestionsFeatureEnabled={isQuestionsFeatureEnabled}
        reviews={
          <ManyReviewsTeaser
            fullUrl={fullUrl as string}
            name={name}
            rating={reviews.rating}
            reviewsCountText={reviews.reviewsCountText}
            reviews={reviews.reviews}
            sections={reviews.sections}
            showReviewsLinkClick={trackingShowReviewsLinkClick}
            readReviewLinkClick={trackingReadAllClick}
            masterReview={<MasterReviewContainer />}
            isQuestionsFeatureEnabled={isQuestionsFeatureEnabled}
            title={reviews.title}
          />
        }
      />
    </ActionAfterViewed>
  );
};
